import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDocumentHead } from '@/hooks/use-document-head'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { FeatureFlags } from '@/types/feature-flags'
import {
  agentsApiClient,
  type AgentWithVoiceSettings,
} from '@/lib/api/agents.client'
import { agentsAdminApiClient } from '@/lib/api/agents-admin.client'
import { scenariosApiClient } from '@/lib/api'
import { toast } from 'sonner'
import { EmptyState } from '@/components/ui/empty-state'
import { Badge } from '@/components/ui/badge'
import {
  Bot,
  PhoneIncoming,
  PhoneOutgoing,
  MoreHorizontal,
  Pencil,
  Trash2,
  Pause,
  Play,
  Sparkles,
  FileText,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { confirmDialog } from '@/lib/utils/window-utils'
import {
  type AgentDirection,
  getTemplatesByDirection,
  INBOUND_ARCHETYPE_SCENARIOS,
} from '@super/shared-types'
import type { AgentScenario } from '@super/shared-types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowLeft } from 'lucide-react'
import { formatTriggerLabel } from '@/features/agent-detail'

interface CreateAgentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with (type, optional template scenario). Navigate to /agents/new?type=X&template=Y */
  onComplete: (type: AgentDirection, templateScenario?: string) => void
}

const VALID_SCENARIOS: AgentScenario[] = [
  'LEASING_OUTBOUND',
  'DELINQUENCY_OUTBOUND',
  'LEASE_RENEWAL_OUTBOUND',
  'LEASING_INBOUND',
  'EMERGENCY_OUTBOUND',
  'LEASING_EMAIL_OUTBOUND',
  'LEASING_EMAIL_INBOUND',
  'ROUTING_INBOUND',
  'GATEKEEPER_INBOUND',
  'HYBRID_INBOUND',
  'SKELETON_INBOUND',
]

function isValidScenario(value: string): value is AgentScenario {
  return VALID_SCENARIOS.includes(value as AgentScenario)
}

/**
 * The Skeleton archetype is a blank goal-based scaffold — it is what "start
 * from scratch" means for inbound. When present it backs the scratch option
 * rather than appearing as a selectable template in the dropdown.
 */
const SCRATCH_SKELETON_SCENARIO: AgentScenario = 'SKELETON_INBOUND'

function CreateAgentModal({
  open,
  onOpenChange,
  onComplete,
}: CreateAgentModalProps) {
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedType, setSelectedType] = useState<AgentDirection | null>(null)
  const [selectedTemplateScenario, setSelectedTemplateScenario] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (open) {
      setStep(1)
      setSelectedType(null)
      setSelectedTemplateScenario(null)
    }
  }, [open])

  const templates =
    selectedType != null
      ? getTemplatesByDirection(selectedType).filter(t => {
          if (!t.isActive || t.comingSoon) return false
          // Inbound picker only surfaces the curated receptionist archetypes.
          if (selectedType === 'INBOUND')
            return INBOUND_ARCHETYPE_SCENARIOS.includes(t.scenario)
          return true
        })
      : []

  // "Start from scratch" is the absence of a template. For inbound that means
  // the blank Skeleton scaffold; for outbound it means a truly blank agent with
  // no template to seed from. The Skeleton is therefore surfaced as the scratch
  // option rather than as a row in the template dropdown below.
  const hasSkeletonTemplate = templates.some(
    t => t.scenario === SCRATCH_SKELETON_SCENARIO,
  )
  const templateOptions = templates
    .filter(t => t.scenario !== SCRATCH_SKELETON_SCENARIO)
    .map(t => ({
      value: t.scenario,
      name: t.name,
      description: t.description,
    }))
  const hasTemplateOptions = templateOptions.length > 0
  const selectedTemplateOption = templateOptions.find(
    o => o.value === selectedTemplateScenario,
  )

  const handleSelectType = (type: AgentDirection) => {
    // Show the "template vs scratch" step for both inbound and outbound.
    setSelectedType(type)
    setSelectedTemplateScenario(null)
    setStep(2)
  }

  const handleStartFromTemplate = () => {
    if (
      !selectedType ||
      !selectedTemplateScenario ||
      !isValidScenario(selectedTemplateScenario)
    )
      return
    onComplete(selectedType, selectedTemplateScenario)
    onOpenChange(false)
  }

  const handleStartFromScratch = () => {
    if (!selectedType) return
    // Inbound "start from scratch" is the blank Skeleton scaffold; outbound
    // gets a truly blank agent with no template to seed from.
    if (hasSkeletonTemplate) {
      onComplete(selectedType, SCRATCH_SKELETON_SCENARIO)
    } else {
      onComplete(selectedType)
    }
    onOpenChange(false)
  }

  const handleBack = () => {
    setStep(1)
    setSelectedType(null)
    setSelectedTemplateScenario(null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle>Choose the type of agent</DialogTitle>
              <DialogDescription>
                Select the type of agent you want to create
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <button
                type="button"
                onClick={() => handleSelectType('INBOUND')}
                className={cn(
                  'flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left',
                  'hover:border-primary hover:bg-accent border-border',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                )}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <PhoneIncoming className="w-5 h-5 text-primary" />
                </div>
                <div className="font-medium">Inbound</div>
                <div className="text-sm text-muted-foreground">
                  For incoming calls
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectType('OUTBOUND')}
                className={cn(
                  'flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left',
                  'hover:border-primary hover:bg-accent border-border',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                )}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <PhoneOutgoing className="w-5 h-5 text-primary" />
                </div>
                <div className="font-medium">Outbound</div>
                <div className="text-sm text-muted-foreground">
                  For outgoing calls
                </div>
              </button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-2 -ml-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <DialogTitle>How do you want to start?</DialogTitle>
              <DialogDescription>
                Start from a template with pre-built settings or configure from
                scratch.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Template</label>
                <Select
                  value={selectedTemplateScenario ?? ''}
                  onValueChange={value =>
                    setSelectedTemplateScenario(value || null)
                  }
                  disabled={!hasTemplateOptions}
                >
                  <SelectTrigger>
                    {/* Show only the name in the trigger; descriptions live
                        in the dropdown items so the trigger stays single-line. */}
                    <SelectValue placeholder="Select a template...">
                      {selectedTemplateOption?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {templateOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span>{option.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="button"
                onClick={handleStartFromTemplate}
                disabled={!selectedTemplateScenario}
                className="w-full"
              >
                <Sparkles className="w-4 h-4" />
                Start from template
              </Button>

              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground">
                  OR
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleStartFromScratch}
                className="w-full"
              >
                <FileText className="w-4 h-4" />
                Start from scratch
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function AgentsListPage() {
  useDocumentHead({
    title: 'Agents',
    description: 'Manage your AI agents',
  })

  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const { isEnabled } = useFeatureFlag()
  const inboundMultiAgentEnabled = isEnabled(FeatureFlags.INBOUND_MULTI_AGENT)
  const [loading, setLoading] = useState(true)
  const [agents, setAgents] = useState<AgentWithVoiceSettings[]>([])
  const [scenarioNames, setScenarioNames] = useState<Record<string, string>>({})
  const [createModalOpen, setCreateModalOpen] = useState(false)

  // Open create modal if ?create=true is in URL
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setCreateModalOpen(true)
      // Clear the query param
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (currentCompany?.id) {
      loadAgents()
    }
  }, [currentCompany?.id])

  const loadAgents = async () => {
    if (!currentCompany?.id) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      const agentsPromise = isSuperAdmin
        ? agentsAdminApiClient.getAgentsForCompany(currentCompany.id)
        : agentsApiClient.getCompanyAgents()
      const scenariosPromise = inboundMultiAgentEnabled
        ? isSuperAdmin
          ? scenariosApiClient.getScenariosForCompanyByAdmin(currentCompany.id)
          : scenariosApiClient.getScenarios()
        : null

      const [agentsList, scenariosList] = await Promise.all([
        agentsPromise,
        scenariosPromise,
      ])

      // Ensure we always have an array (API or client may return wrong shape)
      const list = Array.isArray(agentsList) ? agentsList : []

      // Default existing agents without direction to INBOUND
      setAgents(
        list.map(agent => ({
          ...agent,
          direction: agent.direction || 'INBOUND',
        })),
      )

      if (scenariosList) {
        setScenarioNames(
          Object.fromEntries(
            scenariosList.map(scenario => [scenario.id, scenario.name]),
          ),
        )
      }
    } catch (error) {
      console.error('Error loading agents:', error)
      toast.error('Failed to load agents')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateComplete = (
    type: AgentDirection,
    templateScenario?: string,
  ) => {
    setCreateModalOpen(false)
    const params = new URLSearchParams({ type })
    if (templateScenario) params.set('template', templateScenario)
    navigate(`/agents/new?${params.toString()}`)
  }

  const handleEditAgent = (agent: AgentWithVoiceSettings) => {
    navigate(`/agents/${agent.id}`)
  }

  const handleToggleActive = async (agent: AgentWithVoiceSettings) => {
    const newActive = !agent.active
    try {
      const updated = isSuperAdmin
        ? await agentsAdminApiClient.updateAgentForCompany(agent.id, {
            active: newActive,
          })
        : await agentsApiClient.updateAgent(agent.id, { active: newActive })

      if (updated) {
        toast.success(
          `Agent "${agent.name}" ${newActive ? 'activated' : 'deactivated'}`,
        )
        setAgents(prev =>
          (Array.isArray(prev) ? prev : []).map(a =>
            a.id === agent.id ? { ...a, active: newActive } : a,
          ),
        )
      } else {
        toast.error('Failed to update agent status')
      }
    } catch (error) {
      console.error('Error toggling agent status:', error)
      toast.error('Failed to update agent status')
    }
  }

  const handleDeleteAgent = async (agent: AgentWithVoiceSettings) => {
    if (!confirmDialog(`Are you sure you want to delete "${agent.name}"?`)) {
      return
    }

    try {
      // Use admin endpoint if super admin, otherwise use regular endpoint
      const success = isSuperAdmin
        ? await agentsAdminApiClient.deleteAgentForCompany(agent.id)
        : await agentsApiClient.deleteAgent(agent.id)

      if (success) {
        toast.success(`Agent "${agent.name}" deleted successfully`)
        // Remove from local state (guard against non-array state)
        setAgents(prev =>
          (Array.isArray(prev) ? prev : []).filter(a => a.id !== agent.id),
        )
      } else {
        toast.error('Failed to delete agent')
      }
    } catch (error) {
      console.error('Error deleting agent:', error)
      toast.error('Failed to delete agent')
    }
  }

  const safeAgents = (Array.isArray(agents) ? agents : [])
    .slice()
    .sort(
      (a, b) =>
        new Date(b.updatedAt ?? 0).getTime() -
        new Date(a.updatedAt ?? 0).getTime(),
    )

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24">
        {/* Header - aligned with Knowledge bases and other screens */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
          <PageHeader title="Manage your agents" />
          <Button
            onClick={() => setCreateModalOpen(true)}
            variant="form"
            size="sm"
            className="h-9 w-fit shrink-0"
          >
            Add agent
          </Button>
        </div>

        {/* Agents Table */}
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : safeAgents.length === 0 ? (
          <EmptyState
            icon={<Bot className="h-8 w-8 text-muted-foreground" />}
            title="No agents yet"
            description="Create your first agent to get started."
            actionLabel="Create your first agent"
            onAction={() => setCreateModalOpen(true)}
            actionButtonVariant="default"
          />
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden lg:table-cell">Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Type</TableHead>
                  {inboundMultiAgentEnabled && (
                    <TableHead className="hidden lg:table-cell">
                      Trigger
                    </TableHead>
                  )}
                  <TableHead className="hidden lg:table-cell">
                    Channel
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Created by
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Updated at
                  </TableHead>
                  <TableHead className="w-[70px]" align="center">
                    Options
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {safeAgents.map(agent => (
                  <TableRow
                    key={agent.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleEditAgent(agent)}
                  >
                    <TableCell>
                      <div className="font-medium">{agent.name}</div>
                      <div className="lg:hidden text-sm text-muted-foreground">
                        {agent.direction === 'OUTBOUND'
                          ? 'Outbound'
                          : 'Inbound'}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge
                        variant={
                          agent.active !== false ? 'default' : 'secondary'
                        }
                        className={cn(
                          'text-xs',
                          agent.active !== false
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {agent.active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground">
                      {agent.direction === 'OUTBOUND' ? 'Outbound' : 'Inbound'}
                    </TableCell>
                    {inboundMultiAgentEnabled && (
                      <TableCell className="hidden lg:table-cell text-muted-foreground">
                        {formatTriggerLabel(agent, scenarioNames)}
                      </TableCell>
                    )}
                    <TableCell className="hidden lg:table-cell text-muted-foreground">
                      {agent.channels?.length
                        ? agent.channels
                            .map(c => c.charAt(0) + c.slice(1).toLowerCase())
                            .join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground">
                      {agent.createdBy || '—'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground">
                      {new Date(agent.updatedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={e => e.stopPropagation()}
                          >
                            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation()
                              handleEditAgent(agent)
                            }}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit agent
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation()
                              handleToggleActive(agent)
                            }}
                          >
                            {agent.active !== false ? (
                              <>
                                <Pause className="w-4 h-4 mr-2" />
                                Deactivate agent
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-2" />
                                Activate agent
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation()
                              handleDeleteAgent(agent)
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete agent
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <CreateAgentModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onComplete={handleCreateComplete}
        />
      </div>
    </div>
  )
}
